/**
 * Base API Client
 * Provides core functionality for all API clients
 */
import axios from 'axios';
import { ApiClientError } from '../types.js';
const GENERIC_SERVER_ERROR_MESSAGE = 'The service is temporarily unavailable. Please try again later.';
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
/**
 * Base API client class
 * Handles authentication, interceptors, retry logic, and error handling
 */
export class BaseApiClient {
    axiosInstance;
    config;
    getAccessToken;
    constructor(config) {
        this.config = config;
        this.getAccessToken = config.getAccessToken;
        // Create axios instance
        this.axiosInstance = axios.create({
            baseURL: config.baseURL,
            timeout: config.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
                ...config.headers
            }
        });
        // Set up interceptors
        this.setupInterceptors();
    }
    /**
     * Set up request and response interceptors
     */
    setupInterceptors() {
        // Request interceptor - add auth token
        this.axiosInstance.interceptors.request.use(async (config) => {
            // Add access token if available
            if (this.getAccessToken) {
                const forced = config.forceTokenRefresh === true;
                const token = forced
                    ? await this.getAccessToken({ forceRefresh: true })
                    : await this.getAccessToken();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            }
            // Apply custom request interceptors
            if (this.config.requestInterceptors) {
                for (const interceptor of this.config.requestInterceptors) {
                    config = (await interceptor(config));
                }
            }
            return config;
        }, error => Promise.reject(error));
        // Response interceptor - transform responses
        this.axiosInstance.interceptors.response.use(async (response) => {
            // Apply custom response interceptors
            if (this.config.responseInterceptors) {
                for (const interceptor of this.config.responseInterceptors) {
                    response = await interceptor(response);
                }
            }
            return response;
        }, async (error) => {
            // Apply custom error interceptors
            if (this.config.errorInterceptors) {
                for (const interceptor of this.config.errorInterceptors) {
                    try {
                        return await interceptor(error);
                    }
                    catch (interceptorError) {
                        this.reportErrorInterceptorFailure(interceptorError);
                    }
                }
            }
            // Transform axios error to API error
            return Promise.reject(this.transformError(error));
        });
    }
    /**
     * Transform Axios error to API error
     */
    transformError(error) {
        const errorData = isRecord(error) ? error : undefined;
        const response = isRecord(errorData?.response) ? errorData.response : undefined;
        const statusCode = typeof response?.status === 'number' ? response.status : undefined;
        if (statusCode !== undefined && statusCode >= 500) {
            return new ApiClientError({
                code: 'SERVER_ERROR',
                message: GENERIC_SERVER_ERROR_MESSAGE,
                statusCode
            });
        }
        const apiError = {
            code: typeof errorData?.code === 'string' ? errorData.code : 'UNKNOWN_ERROR',
            message: typeof errorData?.message === 'string' ? errorData.message : 'An unknown error occurred',
            statusCode
        };
        // Extract error details from response
        if (isRecord(response?.data)) {
            const data = response.data;
            const envelopeError = isRecord(data.error) ? data.error : undefined;
            if (typeof envelopeError?.message === 'string') {
                apiError.message = envelopeError.message;
            }
            else if (typeof data.message === 'string') {
                apiError.message = data.message;
            }
            if (typeof envelopeError?.code === 'string') {
                apiError.code = envelopeError.code;
            }
            else if (typeof data.code === 'string') {
                apiError.code = data.code;
            }
            if (isRecord(envelopeError?.details)) {
                apiError.details = envelopeError.details;
            }
            else if (isRecord(data.details)) {
                apiError.details = data.details;
            }
        }
        return new ApiClientError(apiError);
    }
    /**
     * Execute request with retry logic
     */
    async request(config, retryConfig) {
        const retry = retryConfig || this.config.retry;
        if (!retry) {
            return this.send(config);
        }
        let lastError;
        const maxRetries = retry.retries;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                return await this.send(config);
            }
            catch (error) {
                lastError = error;
                // Check if we should retry
                if (attempt < maxRetries) {
                    const shouldRetry = this.isRetryableMethod(config.method, retry) &&
                        (retry.retryCondition ? retry.retryCondition(error, config) : this.shouldRetry(error));
                    if (!shouldRetry) {
                        throw error;
                    }
                    // Calculate delay
                    const delay = typeof retry.retryDelay === 'function'
                        ? retry.retryDelay(attempt + 1)
                        : retry.retryDelay;
                    // Wait before retry
                    await this.sleep(delay);
                }
            }
        }
        throw lastError;
    }
    /**
     * One request. A `403 INSUFFICIENT_PERMISSIONS` is replayed once with a freshly fetched token
     * (KORA-171): the permissions claim is fixed when a token is issued, so a role granted during
     * the sign-in (the default role of a new member) is missing from the first token and present
     * in the next. A genuinely missing permission fails again and the second error is returned.
     */
    async send(config) {
        try {
            return await this.axiosInstance.request(config);
        }
        catch (error) {
            if (!this.getAccessToken ||
                config.forceTokenRefresh ||
                !(error instanceof ApiClientError) ||
                error.statusCode !== 403 ||
                error.code !== 'INSUFFICIENT_PERMISSIONS') {
                throw error;
            }
            const replay = { ...config, forceTokenRefresh: true };
            return this.axiosInstance.request(replay);
        }
    }
    /**
     * Determine if error should be retried
     */
    shouldRetry(error) {
        // The response interceptor has already turned the axios error into an ApiClientError by the
        // time request() sees it: no `.response`, and `statusCode` is undefined only for a network error.
        if (error instanceof ApiClientError) {
            const { statusCode } = error;
            return statusCode === undefined || statusCode >= 500 || statusCode === 429;
        }
        if (!isRecord(error) || !error.response) {
            // Network error - retry
            return true;
        }
        if (!isRecord(error.response))
            return false;
        const status = error.response.status;
        // Retry on 5xx errors and 429 (rate limit)
        return typeof status === 'number' && (status >= 500 || status === 429);
    }
    /**
     * Automatic retries replay GET/HEAD/PUT/DELETE by default. POST and PATCH require an explicit
     * caller assertion that the relevant endpoint has a server-side idempotency contract.
     */
    isRetryableMethod(method, retry) {
        const normalizedMethod = method?.toUpperCase();
        const isIdempotentMethod = normalizedMethod === 'GET' ||
            normalizedMethod === 'HEAD' ||
            normalizedMethod === 'PUT' ||
            normalizedMethod === 'DELETE';
        return (isIdempotentMethod ||
            (retry.allowNonIdempotentRetry === true &&
                (normalizedMethod === 'POST' || normalizedMethod === 'PATCH')));
    }
    /**
     * Report an interceptor failure without allowing optional diagnostics to alter the original
     * request error contract.
     */
    reportErrorInterceptorFailure(error) {
        try {
            this.config.logger?.warn('API error interceptor failed; using the original API error.', error);
        }
        catch {
            // A consumer diagnostic sink must never mask the original request error.
        }
    }
    /**
     * Sleep utility for retry delays
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    /**
     * GET request
     */
    async get(url, config, retryConfig) {
        const response = await this.request({ ...config, method: 'GET', url }, retryConfig);
        return response.data;
    }
    /**
     * POST request
     */
    async post(url, data, config, retryConfig) {
        const response = await this.request({ ...config, method: 'POST', url, data }, retryConfig);
        return response.data;
    }
    /**
     * PUT request
     */
    async put(url, data, config, retryConfig) {
        const response = await this.request({ ...config, method: 'PUT', url, data }, retryConfig);
        return response.data;
    }
    /**
     * DELETE request
     */
    async delete(url, data, config, retryConfig) {
        const response = await this.request({ ...config, method: 'DELETE', url, data }, retryConfig);
        return response.data;
    }
    /**
     * PATCH request
     */
    async patch(url, data, config, retryConfig) {
        const response = await this.request({ ...config, method: 'PATCH', url, data }, retryConfig);
        return response.data;
    }
    /**
     * Update access token getter
     */
    setAccessTokenGetter(getter) {
        this.getAccessToken = getter;
    }
    /**
     * Get axios instance (for advanced usage)
     */
    getAxiosInstance() {
        return this.axiosInstance;
    }
}
